import { createSelector } from "reselect"
import { get, pick, cloneDeep, isEqual, chain, has, toUpper, size, find, isNull, isArray, filter, omitBy, isEmpty, toLower, differenceWith, map, includes } from "lodash"
import pluralize from "pluralize"
import { Creators as SmartTradeCreators } from "../model"
import { getSecuritiesCount as getFinderSecuritiesCount } from "modules/securities-finder-results/selectors"
import { getFacetedFilter, getValidFilter as getFilterSaverFilters } from "modules/securities-finder/selectors/filter"
import { getCurrentFilterId } from "modules/filter-saver/selectors"
import { formatLongDate } from "helpers/formatters/dates"
import { dollarFormatter, NEGATIVE_FORMATS } from "helpers/formatters/numbers"
import { ITEMS_COLORS, getBackwardsCompatibleSelectedType, getBaseComparisonTypes } from "helpers/comparison"
import { ASSET_TYPES } from "helpers/assets/constants"
import { NORMALIZATION } from "helpers/segments"
import { positionsComparator } from "helpers/securities"
import { OK, LOADING } from "helpers/data-statuses"
import { getActiveStep } from "./steps"
import { getLatestDate, getSelectedDate } from "./dates"
import { getContentSets } from "selectors/contentSets"
import { getModelId } from "./model"
import { classificationsMap } from "helpers/classification"
import { computeSort } from "helpers/securities-search"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { menuContentSets } from "helpers/menu"
import { getIsOpPro } from "modules/account-root/selectors"
import { getResearchTopicDate } from "modules/research-topic-root/selectors/dates"
import { getInstrument, getInstrumentDescriptors, getInstrumentPortfolio, getInstrumentPortfolioPositions, getInstrumentPortfolioLongShortGMVs } from "./instrument"
import { EXPOSURE_CONSTRAINT_WEIGHTINGS } from "helpers/contentSetConstants"
import { squashUnmodeledBaskets } from "helpers/swaps"

const PSEUDO_INFINITY = 1000000000000

function getRuleType({ type, contentSetId }) {
  if (type.indexOf(contentSetId) !== false) {
    return toLower(type.split("-")[0])
  }
  return toLower(type)
}

const getRawSelectedComparisonType = state => get(state, "modules.smartTrade.selectedComparisonType")
export const getSelectedComparisonType = createSelector(
  [getRawSelectedComparisonType],
  (comparisonType) => getBackwardsCompatibleSelectedType(comparisonType, "scorecard"),
)

export const getTradeSize = state => get(state, "modules.smartTrade.setup.tradeSize")
export const getBasketRules = state => get(state, "modules.smartTrade.setup.basketRules")
export const getSmartTradeRules = state => get(state, "modules.smartTrade.setup.smartTradeRules")
export const getOptimizationStatus = state => get(state, "modules.smartTrade.optimizationStatus")
export const getAdjustedSecuritiesCount = state => get(state, "modules.smartTrade.adjustedSecuritiesCount")
export const getCalculatedRefEquity = state => get(state, "global.data.smartTradeRefEquities.data.model.simulation.composition", [])
export const getSegmentNormalization = state => get(state, "modules.smartTrade.segmentNormalization")
export const getSmartTradeErrors = state => get(state, "modules.smartTrade.setup.errors")

const getResearchTopicExperiments = (state) => get(state, "global.data.topicExperiments.data")
const getExperimentId = (_, props) => get(props, "params.experimentId")
const getAccount = (state) => get(state, "global.selected.account")
const getCurrentTopic = (_, props) => get(props, "currentTopic")
const getRawOptimizationPositionDelta = state => get(state, "modules.smartTrade.optimizationData")
const getSetup = state => get(state, "modules.smartTrade.setup")
const getBasketSize = state => get(state, "modules.smartTrade.setup.basketRules.basketSize")
const getIsModified = state => get(state, "modules.smartTrade.isModified")
const getRiskData0 = state => get(state, "global.data.compareTopicSummaryPositionSet0.data.model.simulation")
const getRiskData1 = state => get(state, "global.data.compareTopicSummaryPositionSet1.data.model.simulation")
const getRiskData2 = state => get(state, "global.data.compareTopicSummaryPositionSet2.data.model.simulation")
const getRiskData3 = state => get(state, "global.data.compareTopicSummaryPositionSet3.data.model.simulation")
const getRiskData4 = state => get(state, "global.data.compareTopicSummaryPositionSet4.data.model.simulation")
const getEditedExperimentPositionSet = state => get(state, "global.data.smartTradeExperimentPositions.data.researchTopic.experiment.dates.0")
const getRawClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getRawSort = state => get(state, "modules.securitiesFinderResults.sort")

const getOptimizationPositionDelta = createSelector(
  [getRawOptimizationPositionDelta, getOptimizationStatus],
  (rawOptimizationPositionDelta, status) => status !== OK ? null : rawOptimizationPositionDelta,
)

const getSmartTradeRefEquity = createSelector(
  [getTradeSize, getBasketRules],
  (rawTradeSize, basketRules) => Math.abs(basketRules.basketSize / 100 * rawTradeSize) + Math.abs(rawTradeSize),
)

export const getOptimizationConstraints = createSelector(
  [getBasketRules, getSmartTradeRules, getTradeSize, getSmartTradeRefEquity, getInstrumentPortfolioLongShortGMVs],
  (basketRules, smartTradeRules, tradeSize, smartTradeRefEquity, instrumentPortfolioLongShortGMVs) => {
    const constraints = { fixPositionSetSecurities: true }
    const marketValue = basketRules.basketSize / 100 * tradeSize
    let objective = { minimizeTotalRisk: true }

    if (marketValue < 0) {
      constraints.shortMarketValue = Math.abs(marketValue)
    } else {
      constraints.longMarketValue = Math.abs(marketValue)
    }

    // For long/short portfolios, we need to account for the currently held positions
    // SHORTs for a short basket | LONGs for a long basket
    // because the optimizer constraint is not smart enough to tell the difference
    const { long: longGMV, short: shortGMV } = instrumentPortfolioLongShortGMVs
    if (longGMV && shortGMV) {
      if (marketValue < 0) {
        constraints.shortMarketValue += shortGMV
      } else {
        constraints.longMarketValue += longGMV
      }
    }

    if (basketRules.targetPositions) {
      objective = { targetPositions: basketRules.targetPositions + 1 }
    }

    if (basketRules.maxTradeADV) {
      constraints.maxLiquidationDays = basketRules.maxTradeADV / 100
    }

    if (basketRules.maxConcentration) {
      constraints.maxConcentration = basketRules.maxConcentration / 100
    }

    const validSmartTradeRules = filter(smartTradeRules, ({ min, max }) => !isNull(min) || !isNull(max))

    const factorExposuresConstraints = chain(validSmartTradeRules)
      .filter(rule => getRuleType(rule) === "factor")
      .map(factorRule => {
        const fields = ["id"]
        if (!isNull(factorRule.min)) {
          fields.push("min")
        }
        if (!isNull(factorRule.max)) {
          fields.push("max")
        }
        if (factorRule.contentSetId) {
          fields.push("contentSetId")
        }
        return {
          ...pick(factorRule, fields),
          exposureWeighting: factorRule.isCurrency ? EXPOSURE_CONSTRAINT_WEIGHTINGS.CURRENCY : EXPOSURE_CONSTRAINT_WEIGHTINGS.PERCENT,
        }
      })
      .value()

    if (size(factorExposuresConstraints) > 0) {
      constraints.exposure = factorExposuresConstraints
    }

    const compositionConstraints = chain(validSmartTradeRules)
      .filter(rule => !["factor", "constraint", "beta"].includes(getRuleType(rule)))
      .map(({ id, min, max, type, classificationId, classificationTier }) => ({
        id,
        classificationId,
        classificationTier,
        minEconomicExposure: isNull(min) ? -PSEUDO_INFINITY : min / 100 * smartTradeRefEquity,
        maxEconomicExposure: isNull(max) ? PSEUDO_INFINITY : max / 100 * smartTradeRefEquity,
        type: toUpper(type),
      }))
      .value()

    if (size(compositionConstraints) > 0) {
      constraints.composition = compositionConstraints
    }

    const customConstraints = {}
    if (smartTradeRules) {
      smartTradeRules.filter(rule => rule.type === "constraint").forEach(({ id, max }) => {
        customConstraints[id] = max
      })
      const beta = chain(validSmartTradeRules)
        .filter(rule => rule.type === "beta")
        .keyBy(rule => rule.id)
        .mapValues(({ min, max }) => omitBy({ min, max }, v => isNull(v)))
        .value()
      if (!isEmpty(beta)) {
        customConstraints.beta = beta
      }
    }

    return {
      constraints: {
        ...constraints,
        ...customConstraints,
      },
      objective,
    }
  },
)

export const getOptimizationOptions = createSelector(
  [getInstrumentPortfolioPositions],
  (portfolioEquities) => {
    // A portfolio reference instrument can have an Equity override
    let options = {}
    if (portfolioEquities) {
      const equity = get(portfolioEquities, "equity")
      options = {
        ...(equity && { equity }),
      }
    }
    return options
  },
)

export const getDisplayTradeSize = createSelector(
  [getTradeSize],
  (rawTradeSize) => !rawTradeSize ? 0 : rawTradeSize / 1000000,
)

export const getBaseUrl = createSelector(
  [getCurrentTopic, getAccount],
  (topic, account) => `/${account}/t/${topic.id}/smart-trade/create/finalize`,
)

export const getInstrumentSecurityFilter = createSelector(
  [getInstrumentDescriptors, getInstrumentPortfolio],
  (instrumentDescriptors, instrumentPortfolio) => {
    if (!instrumentPortfolio) {
      return ({ securities: { notIn: [pick(instrumentDescriptors, ["ticker", "mic", "bloombergTicker"])] } })
    }

    return ({ universe: [{ type: "PORTFOLIO", notIn: instrumentPortfolio.alias }] })
  },
)

export const getSecuritiesCount = createSelector(
  [getFinderSecuritiesCount, getAdjustedSecuritiesCount],
  (foundSecurities, adjustedCount) => adjustedCount || foundSecurities || null,
)

export const getChipsInfo = createSelector(
  [getTradeSize, getResearchTopicDate, getSecuritiesCount, getInstrumentDescriptors, getInstrumentPortfolio, getActiveStep, getBasketSize, getFacetedFilter],
  (tradeSize, tradeDate, foundSecuritiesCount, instrumentDescriptors, instrumentPortfolio, activeStep, basketSize, securityFilters) => {
    const chipsInfo = []
    if (tradeDate) {
      chipsInfo.push({ label: formatLongDate(tradeDate), gotoStep: 0 })
    }

    if (tradeSize && basketSize) {
      const basketSizeDollar = tradeSize * basketSize / 100
      const shortId = !isEmpty(instrumentPortfolio) ? "Portfolio" : instrumentDescriptors.ticker
      chipsInfo.push({ label: `${shortId}: ${dollarFormatter(tradeSize, NEGATIVE_FORMATS.PARENTHESES)}, Basket: ${dollarFormatter(basketSizeDollar, NEGATIVE_FORMATS.PARENTHESES)}`, gotoStep: 0 })
    }

    if (activeStep > 0) {
      if (securityFilters) {
        const filterCount = chain(securityFilters).mapValues(value => {
          if (isArray(value)) {
            return size(value)
          }

          if (has(value, "in") && size(value.in) > 0) {
            return size(value.in)
          }

          if (has(value, "notIn") && size(value.notIn) > 0) {
            return size(value.notIn)
          }

          return 1
        })
          .values()
          .sum()
          .value()

        if (filterCount > 0) {
          chipsInfo.push({ label: `${filterCount} ${pluralize("filter", filterCount)}`, gotoStep: 1 })
        }
      }

      if (foundSecuritiesCount !== null) {
        chipsInfo.push({ label: `${foundSecuritiesCount} ${pluralize("security", foundSecuritiesCount)}`, gotoStep: 1 })
      }
    }
    return chipsInfo
  },
)

export const getSmartTradePositionSet = createSelector(
  [getTradeSize, getOptimizationPositionDelta, getInstrumentDescriptors, getInstrumentPortfolio, getInstrumentPortfolioPositions, getEditedExperimentPositionSet, getIsModified, getOptimizationStatus],
  (tradeSize, basketPositionSet, instrumentDescriptors, instrumentPortfolio, instrumentPortfolioPositions, editedExperimentPositionSet, isModified, optimizationStatus) => {
    if (editedExperimentPositionSet && !isModified) {
      return squashUnmodeledBaskets({ dates: [editedExperimentPositionSet] })
    }
    if (!basketPositionSet || (!instrumentDescriptors && !instrumentPortfolioPositions) || optimizationStatus === LOADING) {
      return null
    }

    let smartTradePositionSet = {}

    if (!isEmpty(instrumentPortfolioPositions)) {
      smartTradePositionSet = {
        date: basketPositionSet.date,
        ...pick(instrumentPortfolioPositions, ASSET_TYPES),
      }

      smartTradePositionSet.equities = chain(get(smartTradePositionSet, "equities"))
        .concat(get(basketPositionSet, "equities"))
        .compact()
        .value()

      smartTradePositionSet.indexes = chain(get(smartTradePositionSet, "indexes"))
        .concat(get(basketPositionSet, "indexes"))
        .compact()
        .value()

      if (instrumentPortfolio.equity) {
        smartTradePositionSet.equity = instrumentPortfolio.equity
      }

      return squashUnmodeledBaskets({ dates: [smartTradePositionSet] })
    }

    smartTradePositionSet = cloneDeep(basketPositionSet)

    const instrumentPosition = {
      id: {
        ticker: instrumentDescriptors.ticker,
        mic: instrumentDescriptors.mic,
        bloombergTicker: instrumentDescriptors.bloombergTicker,
        modelProviderId: instrumentDescriptors.modelProviderId,
        internalId: instrumentDescriptors.internalId,
      },
      economicExposure: tradeSize,
    }

    if (smartTradePositionSet.equities) {
      smartTradePositionSet.equities.push(instrumentPosition)
    } else {
      smartTradePositionSet.equities = [instrumentPosition]
    }

    return squashUnmodeledBaskets({ dates: [smartTradePositionSet] })
  },
)

export const getPortfolioBasket = createSelector(
  [getInstrumentPortfolioPositions, getSmartTradePositionSet],
  (instrumentPortfolioPositions, smartTradePositionSet) => {
    return {
      ...smartTradePositionSet,
      dates: [
        {
          date: get(smartTradePositionSet, "dates.0.date"),
          equities: differenceWith(get(smartTradePositionSet, ["dates", 0, "equities"]), get(instrumentPortfolioPositions, "equities"), positionsComparator),
          indexes: differenceWith(get(smartTradePositionSet, ["dates", 0, "indexes"]), get(instrumentPortfolioPositions, "indexes"), positionsComparator),
        },
      ],
    }
  },
)

export const getComparisonItems = createSelector(
  [getInstrumentDescriptors, getInstrumentPortfolio, getInstrumentPortfolioPositions, getTradeSize, getSmartTradePositionSet, getResearchTopicDate, getSegmentNormalization, getPortfolioBasket],
  (instrumentDescriptors, instrumentPortfolio, instrumentPortfolioPositions, tradeSize, smartTradePositionSet, tradeDate, segmentNormalization, portfolioBasket) => {
    if ((!instrumentDescriptors && !instrumentPortfolio) || tradeSize === 0 || !tradeDate) {
      return null
    }

    if (!isEmpty(instrumentPortfolio)) {
      return getPortfolioItems(instrumentPortfolio, instrumentPortfolioPositions, portfolioBasket, tradeDate, tradeSize, smartTradePositionSet)
    }
    return getSecurityItems(instrumentDescriptors, tradeSize, smartTradePositionSet, segmentNormalization)
  },
)

export const getOptimizationPositionSet = createSelector(
  [getResearchTopicDate, getTradeSize, getInstrumentDescriptors, getInstrumentPortfolio],
  (selectedDate, tradeSize, instrumentDescriptors, instrumentPortfolio) => {
    if (!selectedDate || tradeSize === 0 || (!instrumentDescriptors && !instrumentPortfolio)) {
      return null
    }

    if (!instrumentPortfolio) {
      return {
        dates: [
          {
            date: selectedDate,
            equities: [
              {
                id: {
                  ticker: instrumentDescriptors.ticker,
                  mic: instrumentDescriptors.mic,
                  internalId: instrumentDescriptors.internalId,
                  bloombergTicker: instrumentDescriptors.bloombergTicker,
                  modelProviderId: instrumentDescriptors.modelProviderId,
                },
                economicExposure: tradeSize,
              },
            ],
          },
        ],
      }
    }
    return {
      type: "PORTFOLIO",
      id: instrumentPortfolio.id,
    }
  },
)

export const getSmartTradeRisk = createSelector(
  [getRiskData0, getRiskData1, getRiskData2, getRiskData3, getRiskData4, getInstrumentDescriptors, getInstrumentPortfolio],
  (riskData0, riskData1, riskData2, riskData3, riskData4, instrumentDescriptors, instrumentPortfolio) => {
    const { ticker, mic } = instrumentDescriptors || {}

    if (!isEmpty(instrumentPortfolio)) {
      return {
        refInstrument: get(riskData0, "risk.0.attribution.summary.specific"),
        smartTrade: get(riskData2, "risk.0.attribution.summary.specific"),
      }
    }

    const data = chain([
      riskData0,
      riskData1,
      riskData2,
      riskData3,
      riskData4,
    ]).map((summary) => get(summary, "riskContributors"))
      .map((riskContributors) => (
        !riskContributors ? null : get(riskContributors.find(({ id }) => id === `${ticker}.${mic}`), "attribution.summary.specific", null)
      ))
      .value()

    return {
      refInstrument: data[0],
      smartTrade: data[2],
    }
  },
)

export const getScorecardHeadline = createSelector(
  [getSmartTradeRisk, getInstrument, getInstrumentDescriptors, getSelectedComparisonType, getInstrumentPortfolio],
  (risk, instrument, instrumentDescriptors, selectedComparisonType, instrumentPortfolio) => {
    let headline
    if (get(selectedComparisonType, "id") === "scorecard") {
      let change
      let type = "trade"
      let refInstrument = {
        display: instrumentDescriptors.ticker || instrumentDescriptors.name,
        title: instrumentDescriptors.name,
      }
      if (!isEmpty(instrumentPortfolio)) {
        type = "portfolio"
        refInstrument = {
          display: instrumentPortfolio.name,
          title: instrumentPortfolio.name,
        }
      }

      if (risk.refInstrument > risk.smartTrade) {
        change = "decreased"
      } else if (risk.refInstrument < risk.smartTrade) {
        change = "increased"
      } else {
        change = "maintained"
      }

      headline = {
        type,
        change,
        refInstrument,
        risk,
      }
    }
    return headline
  },
)

const getRawComparisonTypes = createSelector(
  [],
  () => [
    { id: "scorecard", name: "Scorecard", base: true },
    ...getBaseComparisonTypes(false),
  ])

export const getComparisonTypes = createSelector(
  [getRawComparisonTypes, getResearchTopicDate, getLatestDate, getContentSets],
  (comparisonTypes, startDate, endDate, contentSets) => {
    // We need to disable the Performance option when its a single day graph
    const isASingleDate = startDate === endDate
    if (isASingleDate) {
      const performanceIndex = comparisonTypes.findIndex((type) => type.id === "performance")
      comparisonTypes[performanceIndex] = Object.assign(
        comparisonTypes[performanceIndex],
        {
          disabled: true,
          tooltip: "Performance data is not available for baskets created today",
        })
    }
    return [
      ...comparisonTypes,
      ...menuContentSets(contentSets),
    ]
  },
)

const getExperimentFilters = createSelector(
  [getFilterSaverFilters, getFacetedFilter, getCurrentFilterId],
  (savedFilters, facetedFilters, filterId) => {
    if (!filterId || !isEqual(savedFilters, facetedFilters)) {
      return { filters: facetedFilters }
    }

    return { filterId }
  },
)

export const getExperiment = createSelector(
  [getSmartTradePositionSet, getSetup, getExperimentFilters, getModelId],
  (smartTradePositionSet, setup, filterConfig, modelId) => {
    if (!smartTradePositionSet) {
      return null
    }
    return {
      type: "SMART_TRADE",
      positionSet: smartTradePositionSet,
      modelId,
      config: {
        setup,
        filterConfig,
      },
    }
  },
)

export const getEditedExperiment = createSelector(
  [getExperimentId, getResearchTopicExperiments],
  (experimentId, experiments) => find(experiments, item => item.id === experimentId),
)

export const getClassificationsTiers = createSelector(
  [getRawClassifications, getResearchTopicDate],
  (classifications, selectedDate) => classificationsMap(classifications, selectedDate),
)

const getSort = createSelector(
  [getRawSort, getMissingDataFiles, getModelId, getIsOpPro],
  computeSort,
)

export const getOptimizationUniverseSort = createSelector(
  [getSort],
  (sort) => {
    const { direction, id, getter } = sort
    return getter ? getter(id, direction) : undefined
  },
)

export const getSmartTradeRefEquities = createSelector(
  [getInstrumentPortfolio, getInstrumentDescriptors, getCalculatedRefEquity, getComparisonItems, getSelectedDate],
  (instrumentPortfolio, instrumentDescriptors, smartTradeGMVs, comparisonItems, selectedDate) => {
    const smartTradeGMV = chain(smartTradeGMVs)
      .find(({ date }) => date === selectedDate)
      .get("gmv")
      .value()

    // For smart portfolios, we always get the data from the composition queries
    if (!isEmpty(instrumentPortfolio)) {
      const getRefOrGMVFromComposition = (item) => item.referenceEquity || item.gmv
      return [
        getRefOrGMVFromComposition,
        getRefOrGMVFromComposition,
        getRefOrGMVFromComposition,
      ]
    }

    // For smart trades (security), we override the composition data with the Smart Trade GMV
    // when the instrument or the basket are viewed as Contributors instead of Standalone
    const standaloneInstrument = (item) => item && !includes(["basket", "smart-trade"], item.id) && item.normalization === NORMALIZATION.STANDALONE
    const standaloneBasket = (item) => item && item.id === "basket" && item.normalization === NORMALIZATION.STANDALONE
    const getGMVFromComposition = (item) => item.gmv

    return map(comparisonItems, (item) => {
      switch (true) {
        case standaloneInstrument(item):
        case standaloneBasket(item):
          return getGMVFromComposition
        default:
          return smartTradeGMV
      }
    })
  },
)

function getSecurityItems(descriptors, tradeSize, smartTradePositionSet, segmentNormalization) {
  return [{
    id: `${descriptors.ticker}.${descriptors.mic}`,
    label: get(descriptors, "name", "Instrument"),
    type: "inline",
    color: ITEMS_COLORS[0],
    uploading: !smartTradePositionSet,
    actions: [{
      label: `Normalize As ${segmentNormalization.refInstrument === NORMALIZATION.STANDALONE ? "Contributor" : "Standalone"}`,
      fn: () => SmartTradeCreators.setSegmentNormalization("refInstrument", segmentNormalization.refInstrument === NORMALIZATION.STANDALONE ? NORMALIZATION.CONTRIBUTION : NORMALIZATION.STANDALONE),
    }],
    normalization: segmentNormalization.refInstrument,
    positionSet: {
      segment: {
        filters: [{ longShort: tradeSize > 0 ? "LONG" : "SHORT" }],
        normalization: toUpper(segmentNormalization.refInstrument),
      },
      ...smartTradePositionSet,
    },
  }, {
    id: "basket",
    type: "inline",
    label: "Basket",
    color: ITEMS_COLORS[1],
    uploading: !smartTradePositionSet,
    normalization: segmentNormalization.basket,
    actions: [{
      label: `Normalize As ${segmentNormalization.basket === NORMALIZATION.STANDALONE ? "Contributor" : "Standalone"}`,
      fn: () => SmartTradeCreators.setSegmentNormalization("basket", segmentNormalization.basket === NORMALIZATION.STANDALONE ? NORMALIZATION.CONTRIBUTION : NORMALIZATION.STANDALONE),
    }],
    positionSet: {
      segment: {
        filters: [{ longShort: tradeSize > 0 ? "SHORT" : "LONG" }],
        normalization: toUpper(segmentNormalization.basket),
      },
      ...smartTradePositionSet,
    },
  }, {
    id: "smart-trade",
    type: "inline",
    label: "Smart Trade",
    color: ITEMS_COLORS[2],
    uploading: !smartTradePositionSet,
    positionSet: smartTradePositionSet,
  }]
}

function getPortfolioItems(instrumentPortfolio, instrumentPortfolioPositions, portfolioBasket, tradeDate, tradeSize, smartTradePositionSet) {
  return [{
    id: instrumentPortfolio.id,
    label: instrumentPortfolio.name,
    type: "inline",
    color: ITEMS_COLORS[0],
    actions: [],
    uploading: !smartTradePositionSet,
    normalization: NORMALIZATION.STANDALONE,
    positionSet: {
      dates: [{
        date: tradeDate,
        ...pick(instrumentPortfolioPositions, ASSET_TYPES, "equity"),
      }],
    },
  }, {
    id: "basket",
    type: "inline",
    label: "Basket",
    color: ITEMS_COLORS[1],
    uploading: !smartTradePositionSet,
    actions: [],
    normalization: NORMALIZATION.STANDALONE,
    positionSet: portfolioBasket,
  }, {
    id: "smart-trade",
    type: "inline",
    label: "Smart Portfolio",
    color: ITEMS_COLORS[2],
    uploading: !smartTradePositionSet,
    positionSet: smartTradePositionSet,
  }]
}
